import { X } from "lucide-react";

/**
 * Table popup to lock / unlock specific financial years.
 * Active year stays open by default (caller should not auto-lock it).
 */
export default function TaxYearLockModal({
  darkMode = false,
  isOpen,
  onClose,
  years = [],
  activeTaxYear,
  lockedTaxYears = [],
  onChangeLocked,
  canEdit = true,
}) {
  if (!isOpen) return null;

  const lockedSet = new Set(lockedTaxYears.map(String));

  const toggleYear = (fy) => {
    if (!canEdit) return;
    const next = lockedSet.has(fy)
      ? lockedTaxYears.filter((y) => y !== fy)
      : [...lockedTaxYears, fy];
    onChangeLocked?.(next);
  };

  return (
    <div className="fixed inset-0 z-[120000] flex items-center justify-center bg-black/25 p-4 backdrop-blur-[1px]">
      <div
        className={`w-full max-w-lg overflow-hidden rounded-2xl border shadow-xl ${
          darkMode
            ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] text-white"
            : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]"
        }`}
      >
        <div className="flex items-center justify-between border-b border-[hsl(var(--ds-border))] px-4 py-3 dark:border-[hsl(var(--ds-border))]">
          <div>
            <h3 className="text-sm font-semibold">Lock / Unlock Tax Years</h3>
            <p className={`text-xs mt-0.5 ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
              Locked years block Tax Planning edits. Active year stays open unless you lock it.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))] transition-colors hover:bg-[hsl(var(--ds-muted))] dark:border-[hsl(var(--ds-border))] dark:text-[hsl(var(--ds-muted-fg))] dark:hover:bg-[hsl(var(--ds-muted))]"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto px-3 py-2">
          <div
            className={`overflow-hidden rounded-xl border ${
              darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"
            }`}
          >
            <table className="w-full text-left text-sm">
              <thead
                className={`border-b ${
                  darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]"
                }`}
              >
                <tr>
                  <th className="px-4 py-2.5 font-semibold">Financial Year</th>
                  <th className="px-4 py-2.5 font-semibold">Status</th>
                  <th className="px-4 py-2.5 font-semibold text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {years.map((fy) => {
                  const isActive = fy === activeTaxYear;
                  const isLocked = lockedSet.has(fy);
                  return (
                    <tr
                      key={fy}
                      className={`border-b last:border-0 ${
                        darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"
                      }`}
                    >
                      <td className="px-4 py-2.5">
                        <span className="font-medium">{fy}</span>
                        {isActive && (
                          <span className="ml-2 rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-[10px] font-semibold text-green-800">
                            Active
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${
                            isLocked
                              ? "bg-red-50 text-red-700"
                              : "bg-green-50 text-green-700 dark:text-green-400"
                          }`}
                        >
                          {isLocked ? "Locked" : "Open"}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <button
                          type="button"
                          disabled={!canEdit}
                          onClick={() => toggleYear(fy)}
                          className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors disabled:opacity-50 ${
                            isLocked
                              ? "border-green-300 bg-green-50 text-green-800 hover:bg-green-100"
                              : "border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
                          }`}
                        >
                          {isLocked ? "Unlock" : "Lock"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {years.length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-4 py-6 text-center text-xs text-[hsl(var(--ds-muted-fg))]">
                      No financial years available.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex justify-end border-t border-[hsl(var(--ds-border))] px-4 py-3 dark:border-[hsl(var(--ds-border))]">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-4 py-1.5 text-xs font-medium transition-colors hover:bg-[hsl(var(--ds-muted))] dark:border-[hsl(var(--ds-border))] dark:bg-[hsl(var(--ds-muted))] dark:hover:bg-[hsl(var(--ds-muted))]"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
